import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, loadBuildingsForSelector } from '../../../api';
import { useAuth } from '../../../contexts/AuthContext';
import {applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../../utils/buildingSelection';
import {
  incomeExpenseApi,
  IeCategory,
  IeCategoryProjectBankMode,
  ProjectBankAccount,
} from '../../../api/incomeExpenseApi';

interface Building {
  id: string;
  name: string;
}

interface CategoryListTabProps {
  type: 'revenue' | 'expenditure';
}

const categoryBankSelectValue = (cat: IeCategory): string => {
  if (cat.projectBankAccountMode === 'STAFF_SELECT') return 'STAFF_SELECT';
  if (
    cat.projectBankAccountMode === 'USE_BUILDING_DEFAULT' ||
    (!cat.projectBankAccountMode && !cat.projectBankAccountId)
  ) {
    return 'USE_BUILDING_DEFAULT';
  }
  return cat.projectBankAccountId || 'USE_BUILDING_DEFAULT';
};

const bindingFromSelectValue = (
  value: string,
): { projectBankAccountMode: IeCategoryProjectBankMode; projectBankAccountId: string | null } => {
  if (value === 'STAFF_SELECT') {
    return { projectBankAccountMode: 'STAFF_SELECT', projectBankAccountId: null };
  }
  if (value === 'USE_BUILDING_DEFAULT') {
    return { projectBankAccountMode: 'USE_BUILDING_DEFAULT', projectBankAccountId: null };
  }
  return { projectBankAccountMode: 'FIXED_ACCOUNT', projectBankAccountId: value };
};

const formatAccountLabel = (account: Pick<ProjectBankAccount, 'name' | 'accountType' | 'isDefault'>) => {
  const parts = [account.name];
  if (account.accountType) parts.push(account.accountType);
  if (account.isDefault) parts.push('(Mặc định)');
  return parts.join(' · ');
};

export const CategoryListTab: React.FC<CategoryListTabProps> = ({ type }) => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [buildingId, setBuildingId] = useState('');
  const [categories, setCategories] = useState<IeCategory[]>([]);
  const [bankAccounts, setBankAccounts] = useState<ProjectBankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', bankBinding: 'USE_BUILDING_DEFAULT' });
  const [editing, setEditing] = useState<IeCategory | null>(null);

  const apiMethods = useMemo(
    () =>
      type === 'revenue'
        ? {
            list: incomeExpenseApi.listRevenueCategories,
            create: incomeExpenseApi.createRevenueCategory,
            update: incomeExpenseApi.updateRevenueCategory,
            remove: incomeExpenseApi.deleteRevenueCategory,
            seed: incomeExpenseApi.seedRevenueCategories,
          }
        : {
            list: incomeExpenseApi.listExpenditureCategories,
            create: incomeExpenseApi.createExpenditureCategory,
            update: incomeExpenseApi.updateExpenditureCategory,
            remove: incomeExpenseApi.deleteExpenditureCategory,
            seed: incomeExpenseApi.seedExpenditureCategories,
          },
    [type],
  );

  useEffect(() => {
    loadBuildingsForSelector().then((data) => {
      const list = data || [];
      setBuildings(list);
      setBuildingId((prev) => applyDefaultBuildingSelection(list, user, prev));
    });
  }, [user]);

  const loadBankAccounts = useCallback(async () => {
    if (!buildingId) {
      setBankAccounts([]);
      return;
    }
    try {
      setBankAccounts(await incomeExpenseApi.listProjectBankAccounts(buildingId));
    } catch {
      setBankAccounts([]);
    }
  }, [buildingId]);

  const loadCategories = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const data = await apiMethods.list(buildingId);
      setCategories(data || []);
    } catch {
      toast.error('Không thể tải danh sách loại');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [buildingId, apiMethods]);

  useEffect(() => {
    loadBankAccounts();
    loadCategories();
  }, [loadBankAccounts, loadCategories]);

  const defaultAccountLabel = useMemo(() => {
    const defaultAccount = bankAccounts.find((account) => account.isDefault) || bankAccounts[0];
    return defaultAccount ? formatAccountLabel(defaultAccount) : 'Tài khoản mặc định';
  }, [bankAccounts]);

  const handleSave = async () => {
    if (!buildingId || !form.code.trim() || !form.name.trim()) {
      toast.error('Vui lòng nhập đủ mã và tên');
      return;
    }
    try {
      const bankBinding = bindingFromSelectValue(form.bankBinding);
      const payload = {
        name: form.name.trim(),
        ...bankBinding,
      };
      if (editing) {
        await apiMethods.update(editing.id, payload);
      } else {
        await apiMethods.create({
          buildingId,
          code: form.code.trim(),
          name: form.name.trim(),
          ...bankBinding,
        });
      }
      toast.success('Đã lưu');
      setShowForm(false);
      setEditing(null);
      setForm({ code: '', name: '', bankBinding: 'USE_BUILDING_DEFAULT' });
      loadCategories();
    } catch {
      toast.error('Không thể lưu loại');
    }
  };

  const handleAccountChange = async (category: IeCategory, value: string) => {
    try {
      await apiMethods.update(category.id, bindingFromSelectValue(value));
      loadCategories();
    } catch {
      toast.error('Không thể cập nhật tài khoản dự án');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {shouldShowBuildingSelector(buildings) && (
        <select
          value={buildingId}
          onChange={(e) => selectBuilding(user?.id, e.target.value, setBuildingId)}
                disabled={!needsBuildingSelection(buildings)}
          className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
        >
          {buildings.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
        )}
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setForm({ code: '', name: '', bankBinding: 'USE_BUILDING_DEFAULT' });
            setShowForm(true);
          }}
          className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
        >
          + Thêm
        </button>
        <button
          type="button"
          onClick={async () => {
            if (!buildingId) return;
            try {
              await apiMethods.seed(buildingId);
              toast.success('Đã tạo loại mặc định');
              loadCategories();
            } catch {
              toast.error('Không thể tạo loại mặc định');
            }
          }}
          className="px-4 py-2 rounded-lg border border-cyan-300 text-cyan-700 text-sm font-medium hover:bg-cyan-50"
        >
          Tạo loại mặc định
        </button>
      </div>

      <div className="text-sm text-gray-600 bg-cyan-50 border border-cyan-100 rounded-lg px-4 py-3">
        Mỗi loại thu/chi có thể: <strong>không gán</strong> (nhân viên chọn tài khoản khi tạo phiếu),{' '}
        <strong>dùng tài khoản mặc định</strong> ({defaultAccountLabel}), hoặc <strong>gán cố định</strong> một tài
        khoản.
      </div>

      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={form.code}
            disabled={!!editing}
            onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))}
            placeholder="Mã"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm disabled:bg-gray-100"
          />
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Tên"
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          />
          <select
            value={form.bankBinding}
            onChange={(e) => setForm((f) => ({ ...f, bankBinding: e.target.value }))}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm"
          >
            <option value="STAFF_SELECT">Không gán — chọn khi tạo phiếu</option>
            <option value="USE_BUILDING_DEFAULT">Dùng tài khoản mặc định</option>
            {bankAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                Gán: {formatAccountLabel(account)}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={handleSave} className="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm">Lưu</button>
            <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded-lg text-sm">Hủy</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Mã</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tên</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tài khoản dự án</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">Đang tải...</td></tr>
            ) : categories.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-gray-500">Chưa có dữ liệu</td></tr>
            ) : (
              categories.map((cat) => (
                <tr key={cat.id} className="border-t">
                  <td className="px-4 py-3 text-sm font-mono">{cat.code}</td>
                  <td className="px-4 py-3 text-sm">{cat.name}</td>
                  <td className="px-4 py-3 text-sm min-w-[220px]">
                    <select
                      value={categoryBankSelectValue(cat)}
                      onChange={(e) => handleAccountChange(cat, e.target.value)}
                      className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                    >
                      <option value="STAFF_SELECT">Không gán — chọn khi tạo phiếu</option>
                      <option value="USE_BUILDING_DEFAULT">Dùng tài khoản mặc định</option>
                      {bankAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          Gán: {formatAccountLabel(account)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(cat);
                          setForm({
                            code: cat.code,
                            name: cat.name,
                            bankBinding: categoryBankSelectValue(cat),
                          });
                          setShowForm(true);
                        }}
                        className="text-orange-500 hover:text-orange-600"
                        title="Sửa"
                      >
                        ✏️
                      </button>
                      {!cat.isSystem && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (!window.confirm('Xóa loại này?')) return;
                            await apiMethods.remove(cat.id);
                            loadCategories();
                          }}
                          className="text-red-500 hover:text-red-600"
                          title="Xóa"
                        >
                          🗑️
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={async () => {
                          await apiMethods.update(cat.id, { isActive: !cat.isActive });
                          loadCategories();
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cat.isActive ? 'bg-cyan-500' : 'bg-gray-300'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${cat.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">Tổng {categories.length}</div>
      </div>
    </div>
  );
};

export default CategoryListTab;
